import React from "react";
import { Droplet, Wheat, Heart } from "lucide-react";

/* OUR ROUTE — animated milestone strip with truck driving along a dashed path,
   ending at the pink flag. Three pin milestones with brand-coloured icons. */

const TRUCK_SRC = "/scene/truck.png";

const PINS = [
  { Icon: Droplet, color: "#e5197f", title: "NOURISHING", desc: "Natural ingredients that care." },
  { Icon: Wheat,   color: "#f26b1e", title: "ROOTED",     desc: "Inspired by heritage, made with purpose." },
  { Icon: Heart,   color: "#7a3cff", title: "MADE WITH LOVE", desc: "Small batches. Big difference." },
];

const Pin = ({ Icon, color }) => (
  <div className="relative flex flex-col items-center">
    <div
      className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-[0_8px_18px_-6px_rgba(11,27,61,0.45)]"
      style={{ background: color }}
    >
      <Icon className="w-6 h-6 text-white" />
      <div
        className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45"
        style={{ background: color }}
      />
    </div>
    <div className="mt-3 w-10 h-1.5 rounded-full bg-[#0b1b3d]/15 blur-[1px]" />
  </div>
);

export const OurRoute = () => {
  return (
    <section className="relative max-w-7xl mx-auto px-4 sm:px-8 py-10" data-testid="our-route">
      <div className="flex items-center justify-center gap-3 text-[#e5197f]">
        <span className="h-px w-10 bg-[#e5197f]/40" />
        <h2 className="font-display tracking-[0.4em] text-sm sm:text-base">OUR ROUTE</h2>
        <span className="h-px w-10 bg-[#e5197f]/40" />
      </div>

      <div className="relative mt-10 h-44 sm:h-48">
        {/* dashed path */}
        <svg
          viewBox="0 0 1200 180"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
        >
          <path
            d="M 20 130 C 200 100, 340 150, 470 110 S 720 70, 860 110 S 1100 140, 1180 90"
            fill="none"
            stroke="#e5197f"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="2 9"
          />
        </svg>

        {/* truck driving along the path */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none" aria-hidden="true">
          <img
            src={TRUCK_SRC}
            alt=""
            className="absolute drop-shadow-[0_10px_14px_rgba(11,27,61,0.3)]"
            style={{
              width: "150px",
              height: "auto",
              left: 0,
              top: 0,
              offsetPath:
                "path('M 20 130 C 200 100, 340 150, 470 110 S 720 70, 860 110 S 1100 140, 1180 90')",
              offsetRotate: "0deg",
              animation: "routeTruck 12s ease-in-out infinite",
              transform: "translate(-60%, -65%)",
            }}
          />
        </div>

        {/* milestone pins along the path */}
        <div className="absolute inset-0 grid grid-cols-3 items-center px-[18%] pointer-events-none">
          {PINS.map((p) => (
            <div key={p.title} className="flex justify-center">
              <Pin Icon={p.Icon} color={p.color} />
            </div>
          ))}
        </div>

        {/* finish flag */}
        <div className="absolute right-2 bottom-10 flex flex-col items-center">
          <div className="relative">
            <div className="w-1 h-12 bg-[#0b1b3d] rounded-full" />
            <div className="absolute -top-1 left-1 w-8 h-6 bg-[#e5197f] [clip-path:polygon(0_0,100%_50%,0_100%)]" />
          </div>
          <div className="mt-1 w-6 h-1 rounded-full bg-[#0b1b3d]/20 blur-[1px]" />
        </div>
      </div>

      {/* labels */}
      <div className="grid grid-cols-3 gap-6 px-[10%] mt-4">
        {PINS.map((p) => (
          <div
            key={p.title}
            className="text-center"
            data-testid={`route-pin-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <h3
              className="font-display tracking-[0.2em] text-sm sm:text-base"
              style={{ color: p.color }}
            >
              {p.title}
            </h3>
            <p className="mt-1 text-sm text-[#0b1b3d]/75 leading-snug max-w-[18ch] mx-auto">
              {p.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default OurRoute;
